import { CheckIcon, LockIcon, SparklesIcon } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import {
  type LadderStage,
  type LadderTopic,
  ladderStagePct,
  SYSTEM_DESIGN_LADDER,
} from '../data/mastery-data'

function StageMeter({ value }: { value: number }) {
  return (
    <div className="h-1.5 w-full max-w-56 overflow-hidden rounded-full bg-muted">
      <div
        className="h-full rounded-full bg-primary transition-all"
        style={{ width: `${value}%` }}
      />
    </div>
  )
}

function TopicChip({ topic }: { topic: LadderTopic }) {
  const done = topic.subtopics.filter((st) => st.mastered).length
  const total = topic.subtopics.length
  const mastered = done === total && total > 0
  const pct = total === 0 ? 0 : Math.round((done / total) * 100)

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium',
            topic.locked && 'text-muted-foreground/60',
            !topic.locked && mastered && 'border-primary/50 bg-primary/5',
            !topic.locked && !mastered && done > 0 && 'border-primary/50',
          )}
        >
          {topic.locked ? (
            <LockIcon
              className="size-3 text-muted-foreground/60"
              aria-hidden="true"
            />
          ) : mastered ? (
            <CheckIcon
              className="size-3 text-task-exercise"
              aria-hidden="true"
            />
          ) : null}
          {topic.name}
          <span className="h-1 w-10 overflow-hidden rounded-full bg-muted">
            <span
              className="block h-full rounded-full bg-primary"
              style={{ width: `${pct}%` }}
            />
          </span>
          <span className="sr-only">
            {topic.locked
              ? 'Locked.'
              : `${done} of ${total} subtopics mastered.`}
          </span>
        </span>
      </TooltipTrigger>
      <TooltipContent>
        {topic.locked
          ? 'Locked — clear earlier topics first'
          : `${done}/${total} subtopics mastered`}
      </TooltipContent>
    </Tooltip>
  )
}

function StageCard({
  stage,
  index,
  total,
}: {
  stage: LadderStage
  index: number
  total: number
}) {
  const pct = ladderStagePct(stage)

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-xl border p-4 md:p-5',
        stage.current &&
          'border-primary/60 bg-primary/[0.03] ring-1 ring-primary/20',
        stage.comingSoon && 'border-dashed',
      )}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="text-xs tabular-nums text-muted-foreground">
          {`Stage ${index + 1} of ${total}`}
        </span>
        <span className="text-sm font-semibold">
          {stage.name}
          {stage.current && (
            <span className="text-primary"> — you are here</span>
          )}
        </span>
        {stage.comingSoon ? (
          <Badge variant="secondary">
            <SparklesIcon aria-hidden="true" />
            Coming soon
          </Badge>
        ) : (
          <span className="flex flex-1 items-center gap-3">
            <StageMeter value={pct} />
            <span className="text-xs font-medium tabular-nums text-muted-foreground">
              {pct}%
            </span>
          </span>
        )}
      </div>

      <p className="text-xs text-muted-foreground">{stage.description}</p>

      {stage.topics.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {stage.topics.map((topic) => (
            <TopicChip key={topic.id} topic={topic} />
          ))}
        </div>
      )}
    </div>
  )
}

export function TierLadder() {
  return (
    <div className="flex flex-col gap-3">
      {SYSTEM_DESIGN_LADDER.map((stage, i) => (
        <StageCard
          key={stage.id}
          stage={stage}
          index={i}
          total={SYSTEM_DESIGN_LADDER.length}
        />
      ))}
    </div>
  )
}
